import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, createBlock as _createBlock, vShow as _vShow, withDirectives as _withDirectives, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withCtx as _withCtx, resolveDirective as _resolveDirective } from "vue"

const _hoisted_1 = ["data-test"]
const _hoisted_2 = { class: "input-text__label tw-inline-block" }
const _hoisted_3 = {
  key: 0,
  class: "input-text__icon-container"
}
const _hoisted_4 = {
  class: "tw-flex tw-align-center tw-min-w-0 tw-flex-1",
  "data-test": "select-multiple-input-container"
}
const _hoisted_5 = {
  class: /*@__PURE__*/_normalizeClass([
              'tw-flex tw-flex-col tw-gap-2',
              'tw-w-full',
            ]),
  "data-test": "select-multiple-selected-value"
}
const _hoisted_6 = {
  key: 0,
  class: "tw-flex tw-flex-col sm:tw-flex-row tw-flex-wrap tw-gap-1"
}
const _hoisted_7 = ["disabled", "placeholder", "value"]
const _hoisted_8 = { class: "input-text__icon-container" }
const _hoisted_9 = { class: "input-text__icon-container input-text__icon-container--small" }
const _hoisted_10 = { class: "input-text__icon-container" }

import CoreChipInput from 'frontcore/views/chips/chip/views/CoreChipInput.vue'
import CoreContextMenu from 'frontcore/views/context-menu/CoreContextMenu.vue'
import DropDownMenuMultipleSelection from 'frontcore/views/drop-down/views/unit/DropDownMenuMultipleSelection.vue'
import InputHelperText from "frontcore/views/select/InputHelperText.vue"
import mitt from 'mitt'
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import useSelectSelection from 'frontcore/views/select/useSelectSelection'
import useSelectState from 'frontcore/views/select/useSelectState'
import { ContextMenuPosition } from 'frontcore/views/context-menu/useContextMenuPosition'
import { DropDownMenuItemProps } from 'frontcore/views/drop-down/domain/types'
import { DropDownMenuScrollEvents, dropDownMenuScrollKey } from 'frontcore/views/drop-down/views/base/types'
import { nextTick, provide, reactive, ref, toRefs, watch } from 'vue'

interface Props {
  dataTest?: string
  disabled?: boolean
  error?: string
  extraItems?: DropDownMenuItemProps[]
  iconName?: string
  items: DropDownMenuItemProps[]
  label?: string
  nullable?: boolean
  placeholder: string
  supportingText?: string
  value: DropDownMenuItemProps['id'][]
}
type Emits = (e: 'input', id: DropDownMenuItemProps['id'][]) => void

export default /*@__PURE__*/_defineComponent({
  __name: 'SelectMultiple',
  props: {
    dataTest: { default: 'select-multiple' },
    disabled: { type: Boolean, default: false },
    error: {},
    extraItems: { default: () => [] },
    iconName: {},
    items: {},
    label: { default: '' },
    nullable: { type: Boolean, default: false },
    placeholder: {},
    supportingText: {},
    value: {}
  },
  emits: ["input"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const { dataTest, disabled, error, iconName, items, value, nullable, extraItems } = toRefs(props)

const emit = __emit

const emitter = mitt<DropDownMenuScrollEvents>()
provide(dropDownMenuScrollKey, emitter)

const {
  emitSearchValue,
  filteredDropDownItems,
  searchText,
  selectedItem,
} = useSelectSelection(reactive({ dataTest, extraItems, iconName, items, value }), true)

const {
  chevronStyle,
  clickOutside,
  isFocused,
  showInput,
  stateStyleClass,
  toggleFocus,
} = useSelectState(reactive({ disabled, error, value, nullable }))

const searchInput = ref<HTMLInputElement | null>(null)
watch(
  () => isFocused.value,
  (newValue, oldValue) => {
    if (!newValue || newValue === oldValue) { return }
    nextTick(() => searchInput.value?.focus())
  }
)

const onClickOption = (selectedIds: DropDownMenuItemProps['id'][]) => {
  if (props.disabled) return
  emit('input', selectedIds)
}

const rootElement = ref<HTMLDivElement | null>(null)
const willToggleFocus = () => {
  toggleFocus()
  clearSearchText()
}

const onInputClick = () => {
  if (!isFocused.value) {
    toggleFocus()
  }
  emitNativeClick()
}

const onChipClick = (itemId: DropDownMenuItemProps['id']) => {
  if (!isFocused.value) {
    willToggleFocus()
  }
  scrollToItem(itemId)
}

const scrollToItem = (itemId: DropDownMenuItemProps['id']) => {
  nextTick(() => {
    emitter.emit('scrollToItemId', itemId)
  })
}

const onChipHandlerClick = (selectedId: DropDownMenuItemProps['id']) => {
  if (props.disabled) { return }
  const selectedIds = props.value.filter((id) => id !== selectedId)
  emit('input', selectedIds)
  emitNativeClick()
}

const clearSearchText = () => {
  searchText.value = null
  emitNativeClick()
  if (isFocused.value) {
    searchInput.value?.focus()
  } else {
    searchInput.value?.blur()
  }
}

const emitNativeClick = () => {
  rootElement.value?.click()
}

const contextMenuAnchor = ref<HTMLElement | null>(null)
const contextMenuPositions: ContextMenuPosition[] = [ContextMenuPosition.bottom, ContextMenuPosition.top]

return (_ctx: any,_cache: any) => {
  const _directive_click_outside = _resolveDirective("click-outside")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["input-text__container", [_unref(stateStyleClass), {'input-text__state-disabled': _unref(disabled)}]]),
    "data-test": _unref(dataTest)
  }, [
    _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.label), 1),
    _withDirectives((_openBlock(), _createElementBlock("div", {
      ref_key: "rootElement",
      ref: rootElement,
      class: _normalizeClass({'pointer': !_unref(disabled)})
    }, [
      _createElementVNode("div", {
        ref_key: "contextMenuAnchor",
        ref: contextMenuAnchor,
        class: _normalizeClass([
          'input-text__input-container',
          {'tw-py-1.5': _unref(value).length > 0},
        ]),
        onClick: _withModifiers(willToggleFocus, ["stop"])
      }, [
        (_unref(iconName))
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(SVGIcon, {
                class: "input-text__left-icon",
                iconName: _unref(iconName),
                remSize: 1.25
              }, null, 8, ["iconName"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            (props.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.value, (item) => {
                    return (_openBlock(), _createBlock(CoreChipInput, {
                      key: item,
                      dataTest: `chip-list-item--${_unref(selectedItem)(item)?.text.replace(/\s+/g, '') ?? ''}`,
                      disabled: _unref(disabled),
                      label: _unref(selectedItem)(item)?.text ?? '',
                      iconName: _unref(selectedItem)(item)?.iconName,
                      imageSrc: _unref(selectedItem)(item)?.imageSrc,
                      class: "tw-truncate tw-max-w-full",
                      deletable: "",
                      selected: "",
                      onClick: _withModifiers(() => { onChipClick(item) }, ["stop"]),
                      onHandlerClick: _withModifiers(() => onChipHandlerClick(item), ["stop"])
                    }, null, 8, ["dataTest", "disabled", "label", "iconName", "imageSrc", "onClick", "onHandlerClick"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            _withDirectives(_createElementVNode("input", {
              ref_key: "searchInput",
              ref: searchInput,
              class: "input-text__input tw-truncate",
              "data-test": "select-multiple-input-text",
              disabled: _unref(disabled),
              placeholder: _ctx.placeholder,
              value: _unref(searchText) ?? undefined,
              type: "text",
              onClick: _withModifiers(onInputClick, ["stop"]),
              onInput: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(emitSearchValue) && _unref(emitSearchValue)(...args)))
            }, null, 40, _hoisted_7), [
              [_vShow, _unref(showInput)]
            ])
          ])
        ]),
        _withDirectives(_createElementVNode("div", _hoisted_8, [
          _createVNode(SVGIcon, {
            class: "input-text__right-icon fill-auxiliary-error-30",
            iconName: "Error",
            remSize: 1.25
          })
        ], 512), [
          [_vShow, _unref(error)]
        ]),
        _withDirectives(_createElementVNode("div", _hoisted_9, [
          _createVNode(SVGIcon, {
            "data-test": "clear-button",
            iconName: "Close",
            colorClass: _unref(disabled) ? 'fill-core-neutral-cold-50' : 'fill-core-neutral-cold-40',
            remSize: 1,
            onClick: _withModifiers(clearSearchText, ["stop"])
          }, null, 8, ["colorClass"])
        ], 512), [
          [_vShow, _unref(searchText) && _unref(showInput)]
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createVNode(SVGIcon, {
            class: "input-text__chevron",
            iconName: "ArrowDropDown",
            colorClass: _unref(disabled) ? 'fill-core-neutral-cold-50' : 'fill-core-neutral-cold-20',
            remSize: 1.25,
            style: _normalizeStyle(_unref(chevronStyle))
          }, null, 8, ["colorClass", "style"])
        ])
      ], 2),
      (_unref(isFocused))
        ? (_openBlock(), _createBlock(CoreContextMenu, {
            key: 0,
            anchor: contextMenuAnchor.value,
            positions: contextMenuPositions,
            withAnchorWidth: ""
          }, {
            default: _withCtx(() => [
              _createVNode(DropDownMenuMultipleSelection, {
                value: _unref(value),
                optionList: _unref(filteredDropDownItems),
                keepOrder: !!_unref(searchText),
                "onInput:value": onClickOption
              }, null, 8, ["value", "optionList", "keepOrder"])
            ], undefined, true),
            _: 1
          }, 8, ["anchor"]))
        : _createCommentVNode("", true)
    ], 2)), [
      [_directive_click_outside, _unref(clickOutside)]
    ]),
    _createVNode(InputHelperText, {
      class: _normalizeClass({invisible: _unref(isFocused)}),
      error: _unref(error),
      supportingText: _ctx.supportingText
    }, null, 8, ["class", "error", "supportingText"])
  ], 10, _hoisted_1))
}
}

})